import { useContext, useEffect } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  dataLocationToPath,
} from '@ea-lab/reactive-json'

/**
 * Regroupe une liste chronologique par MOIS et écrit le résultat dans le store,
 * pour un rendu « timeline » avec séparateurs de mois (« Décembre 2025 »).
 *
 * Pourquoi un élément JSX (échappatoire config-first, justifié) : le
 * regroupement « émettre un en-tête quand le mois change par rapport à l'élément
 * PRÉCÉDENT » n'est pas exprimable en YAML — un `Switch` n'a pas accès à
 * l'élément précédent, et il n'y a pas de reduce/group-by déclaratif. Cet élément
 * lit `source` (un tableau trié desc), le découpe en groupes
 * `[{ monthKey, monthLabel, items: [...] }]` et l'écrit à `target`. Le YAML rend
 * ensuite `target` en `Switch` (en-tête de mois) → `Switch` interne (les items).
 * Générique : aucun couplage CRM/contact.
 *
 * Recalcule quand la signature (nid + date de chaque item) change ; n'écrit
 * jamais en boucle (l'écriture ne modifie pas la source).
 *
 * Props :
 *   - source: chemin du tableau source (ex. ~~.engResult.contents)
 *   - target: chemin où écrire les groupes (ex. ~~.engGrouped)
 *   - dateField: clé de date dans chaque item (défaut « created »)
 *   - idField: clé d'identité pour la signature (défaut « nid »)
 *
 * Rend `null` (invisible). Enregistré dans App.jsx.
 */

// Parse une date façon FormatDate : un entier < 1e12 = timestamp Unix en
// SECONDES (×1000) ; sinon en millisecondes ; sinon chaîne ISO.
const toDate = (value) => {
  if (value == null || value === '') return null
  let d
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const n = Number(value)
    d = new Date(n < 1e12 ? n * 1000 : n)
  } else {
    d = new Date(value)
  }
  return Number.isNaN(d.getTime()) ? null : d
}

const monthLabel = (value) => {
  const d = toDate(value)
  if (!d) return 'Sans date'
  const s = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(d)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const monthKey = (value) => {
  const d = toDate(value)
  if (!d) return 'none'
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const GroupByMonth = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const gdc = globalDataContext?.getRootContext ? globalDataContext.getRootContext() : globalDataContext
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext })

  const raw = resolve(props.source)
  const list = Array.isArray(raw) ? raw : []
  const dateField = props.dateField || 'created'
  const idField = props.idField || 'nid'

  const targetPath = typeof props.target === 'string'
    ? dataLocationToPath({
        dataLocation: props.target,
        currentPath: templateContext?.templatePath,
        globalDataContext: gdc,
        templateContext,
      })
    : null

  // Signature stable : ne recalcule que si l'ordre / les dates changent.
  const sig = JSON.stringify(list.map((it) => [it?.[idField], it?.[dateField]]))

  useEffect(() => {
    if (!targetPath || !gdc?.updateData) return
    const groups = []
    let current = null
    for (const it of list) {
      const key = monthKey(it?.[dateField])
      if (!current || current.monthKey !== key) {
        current = { monthKey: key, monthLabel: monthLabel(it?.[dateField]), items: [] }
        groups.push(current)
      }
      current.items.push(it)
    }
    gdc.updateData(groups, targetPath)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, targetPath])

  return null
}
